/**
 * /academy/progress for the IELTS track (D-105): the four skills in bands (activity rings and a
 * table with the CLB, the gap to the goal and the trend), the estimated overall band, the verdict
 * with its critical points, XP, streak and ranks, the evolution chart in bands and the report.
 * Never mixed with the TCF figures; always an estimate.
 */

import { useAcademyMeRetrieve, useAcademyProgressRetrieve } from "@agencyos/api-client";
import { type IeltsProgress as Progress, lowestClb, parseIeltsProgress } from "@agencyos/core";
import { Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  FileDown,
  Flame,
  MapPin,
  Medal,
  Minus,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  UserRound,
} from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { bandText } from "./band";
import { EvolutionChart } from "../EvolutionChart";
import type { EvolutionPoint } from "../progressTypes";
import { ReportButton } from "../ProgressPage";
import { RankScoreCard } from "../RankScoreCard";
import { dateText } from "../shared";
import { SKILLS } from "../types";
import { ActivityRings, CardTitle, PageHead } from "../ui";
import { IELTS } from "./exam";
import { useIeltsProfile } from "./IeltsHome";

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, stable: ArrowRight, unknown: Minus };
const VERDICT_TONE = { ready: "success", almost: "warning", not_ready: "danger" } as const;

type TFn = ReturnType<typeof useTranslation>["t"];

function criticalText(point: Progress["critical"][number], t: TFn): string {
  const skill = point.skill ? t(`ielts.skill.${point.skill}`) : "";
  switch (point.code) {
    case "not_measured":
      return t("ielts.critical.not_measured", { skill });
    case "below_target":
      return t("ielts.critical.below_target", { skill, gap: bandText(point.gap) });
    case "declining":
      return t("ielts.critical.declining", { skill });
    case "word_count":
      return t("ielts.critical.word_count", { count: point.count ?? 1 });
    case "time":
      return t("ielts.critical.time", { count: point.count ?? 1 });
    case "exam_too_soon":
      return t("ielts.critical.exam_too_soon", { days: point.daysLeft ?? 0, weeks: point.weeksNeeded ?? 1 });
    default:
      return point.message;
  }
}

function Tile({ label, value, hint, icon, tone }: { label: string; value: string; hint?: string; icon: ReactNode; tone: string }) {
  return (
    <div className="ac-stat">
      <span className={`ac-pastille ac-tone--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <div className="ac-stat__body">
        <p className="ac-stat__label">{label}</p>
        <p className="ac-stat__value ao-num">{value}</p>
        {hint && <p className="ac-stat__trend">{hint}</p>}
      </div>
    </div>
  );
}

export function IeltsProgressPage() {
  const { t, i18n } = useTranslation();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  const profile = useIeltsProfile(learner);
  const query = useAcademyProgressRetrieve({ exam: IELTS }, { query: { retry: false } });
  const progress = useMemo(() => (query.data?.status === 200 ? parseIeltsProgress(query.data.data) : null), [query.data]);
  const points: EvolutionPoint[] = useMemo(
    () =>
      (progress?.evolution ?? []).map((row) => ({
        attempt: row.attempt,
        kind: row.kind,
        mock: row.mock,
        at: row.at,
        skills: Object.fromEntries(
          SKILLS.filter((s) => row.skills[s]).map((s) => [s, { score: row.skills[s]?.band ?? null, cefr: row.skills[s]?.cefr ?? "", nclc: row.skills[s]?.band ?? null }]),
        ),
      })),
    [progress],
  );

  if (query.isLoading || me.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 72, maxWidth: 420 }} />
        <Skeleton style={{ height: 300 }} />
      </div>
    );
  }
  if (!progress || !learner) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }
  const target = profile?.target ?? progress.target;
  const reached = SKILLS.filter((s) => {
    const band = progress.skills[s].band;
    return band !== null && band >= target;
  }).length;
  const clb = lowestClb(SKILLS.map((s) => progress.skills[s].clb));
  const VerdictIcon = progress.verdict === "ready" ? CheckCircle2 : progress.verdict === "almost" ? Target : AlertTriangle;

  return (
    <div className="ac-page ac-progress-page">
      <PageHead
        eyebrow={`${t("ielts.exams.ielts_general.name")} · ${t("academy.progress.eyebrow")}`}
        title={t("academy.progress.title")}
        subtitle={
          <ul className="ac-meta-list ac-meta-list--inline">
            <li>
              <UserRound aria-hidden size={15} />
              <span>{learner.full_name || learner.email}</span>
            </li>
            <li>
              <Target aria-hidden size={15} />
              <span>{t("ielts.goal.band", { band: bandText(target) })}</span>
            </li>
            <li>
              <CalendarDays aria-hidden size={15} />
              <span>{profile?.examDate ? dateText(profile.examDate, i18n.language) : t("academy.progress.notSet")}</span>
            </li>
            <li>
              <MapPin aria-hidden size={15} />
              <span>{profile?.examCenter || t("academy.progress.notSet")}</span>
            </li>
          </ul>
        }
      />
      <p className="ac-note" role="note">
        {t("ielts.estimateLong")}
      </p>

      <GlassCard as="section" className="ac-card ac-fitness" aria-labelledby="ielts-skills">
        <div className="ac-card__head">
          <CardTitle id="ielts-skills" icon={<Target size={16} />}>
            {t("ielts.home.ringsTitle")}
          </CardTitle>
          <span className="ac-caption ao-num">{t("academy.ui.progress.reached", { count: reached, total: SKILLS.length })}</span>
        </div>
        <div className="ac-fitness__body">
          <ActivityRings rings={SKILLS.map((skill) => ({ skill, ratio: (progress.skills[skill].band ?? 0) / target }))} size={224} stroke={15}>
            <span className="ac-fitness__center-value ao-num">{bandText(progress.overall)}</span>
            <span className="ac-fitness__center-label">{t("ielts.results.overallShort")}</span>
          </ActivityRings>
          <div className="ac-table-wrap">
            <table className="ac-skill-table">
              <caption className="visually-hidden">{t("ielts.home.ringsTitle")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("academy.progress.skill")}</th>
                  <th scope="col" className="num">
                    {t("ielts.results.bandCol")}
                  </th>
                  <th scope="col" className="num">
                    {t("ielts.progress.clb")}
                  </th>
                  <th scope="col">{t("academy.progress.gap")}</th>
                  <th scope="col">{t("academy.progress.trend")}</th>
                </tr>
              </thead>
              <tbody>
                {SKILLS.map((skill) => {
                  const row = progress.skills[skill];
                  const Icon = TREND_ICON[row.trend];
                  return (
                    <tr key={skill} className={`ac-skill--${skill}`}>
                      <th scope="row">
                        <span className="ac-ring-legend__swatch" aria-hidden="true" />
                        {t(`ielts.skill.${skill}`)}
                      </th>
                      <td className="num ao-num">
                        {row.band !== null ? (
                          <>
                            <strong>{bandText(row.band)}</strong>
                            <span className="ac-tertiary"> / {bandText(target)}</span>
                          </>
                        ) : (
                          t("academy.progress.notMeasured")
                        )}
                      </td>
                      <td className="num ao-num">{row.clb !== null ? row.clb : "—"}</td>
                      <td className={row.gap !== null && row.gap > 0 ? "ac-gap ac-gap--missing" : row.gap !== null ? "ac-gap ac-gap--ok" : "ac-gap"}>
                        {row.gap === null ? "—" : row.gap > 0 ? t("ielts.progress.gapMissing", { gap: bandText(row.gap) }) : t("academy.progress.gapReached")}
                      </td>
                      <td>
                        <span className={`ac-trend ac-trend--${row.trend}`}>
                          <Icon aria-hidden size={15} />
                          {t(`academy.progress.trends.${row.trend}`)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </GlassCard>

      <div className="ac-grid">
        <GlassCard as="section" className={`ac-card ac-span-7 ac-verdict ac-verdict--${progress.verdict}`} aria-labelledby="ielts-verdict">
          <CardTitle id="ielts-verdict" icon={<VerdictIcon size={16} />} tone={VERDICT_TONE[progress.verdict]}>
            {t("academy.verdict.title")}
          </CardTitle>
          <div className="ac-verdict__line">
            <span className="ac-verdict__badge">{t(`academy.verdict.${progress.verdict}`)}</span>
            {progress.daysLeft !== null && progress.daysLeft >= 0 && <span className="ac-caption">{t("academy.verdict.daysLeft", { count: progress.daysLeft })}</span>}
          </div>
          <p>{t(`ielts.verdict.${progress.verdict}`, { band: bandText(target) })}</p>
          <h3 className="ac-label">{t("academy.critical.title")}</h3>
          {progress.critical.length === 0 ? (
            <p className="ok">{t("academy.critical.none")}</p>
          ) : (
            <ul className="ac-critical">
              {progress.critical.map((point, index) => (
                <li key={`${point.code}-${point.skill ?? ""}-${index}`} className={`ac-critical__item ac-critical__item--${point.code === "exam_too_soon" || point.code === "below_target" ? "high" : "mid"}`}>
                  <AlertTriangle aria-hidden size={15} />
                  <span>{criticalText(point, t)}</span>
                </li>
              ))}
            </ul>
          )}
          {clb !== null && <p className="ac-secondary">{t("ielts.results.clbLowest", { n: clb })}</p>}
          <p className="ac-footnote">{t("ielts.verdict.estimate")}</p>
        </GlassCard>
        <div className="ac-span-5 ac-side-stats">
          <Tile label={t("academy.progress.xp")} value={t("academy.progress.xpValue", { xp: progress.xp })} icon={<Sparkles size={16} />} tone="warning" />
          <Tile label={t("academy.progress.streak")} value={t("academy.progress.streakValue", { count: progress.streakDays })} icon={<Flame size={16} />} tone="eo" />
          <Tile
            label={t("academy.progress.rankPlatform")}
            value={progress.platform ? t("academy.progress.position", { count: progress.platform.position, ordinal: true }) : "—"}
            hint={progress.platform ? t("academy.progress.top", { percent: progress.platform.topPercent }) : t("academy.progress.notRanked")}
            icon={<Medal size={16} />}
            tone="info"
          />
          <Tile
            label={t("academy.progress.rankAgency")}
            value={progress.agency ? t("academy.progress.position", { count: progress.agency.position, ordinal: true }) : "—"}
            hint={progress.agency ? t("academy.progress.top", { percent: progress.agency.topPercent }) : t("academy.progress.notRanked")}
            icon={<Trophy size={16} />}
            tone="info"
          />
        </div>
      </div>
      <RankScoreCard id="ielts-rank-score" rank={query.data?.status === 200 ? query.data.data.rank : null} />

      <GlassCard as="section" className="ac-card" aria-labelledby="ielts-evolution">
        <div className="ac-card__head">
          <CardTitle id="ielts-evolution" icon={<TrendingUp size={16} />} tone="success">
            {t("academy.progress.evolution")}
          </CardTitle>
        </div>
        <EvolutionChart points={points} target={target} unit="band" />
      </GlassCard>

      <GlassCard as="section" className="ac-card" aria-labelledby="ielts-report">
        <CardTitle id="ielts-report" icon={<FileDown size={16} />} tone="info">
          {t("academy.report.title")}
        </CardTitle>
        <ReportButton exam="ielts_general" />
      </GlassCard>
    </div>
  );
}
